import * as React from 'react';

import { IButtonStyles, Position } from '@fluentui/react';
import { ISpinButtonStyles } from '@fluentui/react/lib/SpinButton';
import { concatStyleSets, FontWeights, HighContrastSelector, IStyle, ITheme } from '@fluentui/react/lib/Styling';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';

import {
  commonHighContrastDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  getStandardControlSemanticColorsWithTheme,
  scaleBorderWithDpi,
  StandardControlSemanticColors,
  textContainerClassName,
} from '@1js/acui-common';

export const spinButtonOuterContainerStyles: React.CSSProperties = {
  display: 'inline-block',
  width: '100%',
};

const spinButtonDisabledStyles = (theme: ITheme): IStyle => {
  const standardControlSemanticColors: StandardControlSemanticColors = getStandardControlSemanticColorsWithTheme(theme);
  return {
    backgroundColor: standardControlSemanticColors.disabledBackgroundColor,
    pointerEvents: 'none',
    cursor: 'default',
    color: standardControlSemanticColors.inputTextDisabledColor,
  };
};

const spinButtonStyles = (
  theme: ITheme,
  disabled: boolean,
  isFocused: boolean,
  labelPosition: Position
): Partial<ISpinButtonStyles> => {
  const standardControlSemanticColors: StandardControlSemanticColors = getStandardControlSemanticColorsWithTheme(theme);
  return {
    root: [
      getDisplayStyleForRoot(labelPosition),
      {
        alignItems: 'center',
        padding: '0 10px',
        selectors: {
          // Explicity set input padding styles to override styles from WordEditor.css
          // See Bug 2044255
          'input[type=text]': {
            paddingTop: 3,
            paddingRight: 3,
            paddingBottom: 4,
            paddingLeft: 4,
          },
        },
        outline: 'none',
        fontSize: 14,
        width: '100%',
        minWidth: 86,
      },
    ],
    label: [
      textContainerClassName,
      {
        fontSize: 14,
        padding: 0,
        pointerEvents: 'none',
        fontWeight: FontWeights.regular,
        lineHeight: 'inherit',
      },
      disabled && [
        {
          color: standardControlSemanticColors.inputTextDisabledColor,
          cursor: 'default',
        },
        commonHighContrastDisabledStyles,
      ],
    ],
    labelWrapper: [
      {
        height: 40,
        lineHeight: 40,
        alignItems: 'center',
        display: 'inline-flex',
      },
      labelPosition === Position.top && {
        marginBottom: 10,
      },
      labelPosition === Position.bottom && {
        marginTop: 10,
      },
    ],
    spinButtonWrapper: [
      {
        display: 'flex',
        height: 26,
        position: 'static',
        boxSizing: 'content-box',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: standardControlSemanticColors.inputBorderColor,
        backgroundColor: standardControlSemanticColors.inputBackgroundColor,
        width: 70,
        minWidth: 0,
        overflow: 'initial',
        '::after': {
          content: 'none',
        },
      },
      scaleBorderWithDpi(1 /* borderWidthAt100 */),
      !disabled && [
        isFocused && {
          borderColor: standardControlSemanticColors.inputFocusBorderColor,
          outline: '2px dashed transparent',
          overflow: 'hidden',
          [HighContrastSelector]: {
            borderColor: 'Highlight',
          },
          ':active': {
            outline: '2px dashed transparent',
            [HighContrastSelector]: {
              borderColor: 'Highlight',
            },
            button: {
              backgroundColor: standardControlSemanticColors.spinButtonContainerContainerHoverBackground,
            },
          },
          // TODO: Remove once Fabric is changed: https://office.visualstudio.com/OC/_workitems/edit/5291442
          selectors: {
            '&&': {
              borderColor: standardControlSemanticColors.inputFocusBorderColor,
              selectors: {
                ':after': {
                  content: 'none',
                },
              },
            },
          },
        },
        !isFocused && {
          ':hover': {
            outline: '2px dashed transparent',
            [HighContrastSelector]: {
              borderColor: 'Highlight',
            },
            button: {
              backgroundColor: standardControlSemanticColors.spinButtonContainerContainerHoverBackground,
            },
          },
        },
      ],
      disabled && [
        spinButtonDisabledStyles(theme),
        commonHighContrastDisabledStyles,
        isFocused && commonHighContrastOutlineFocusedDisabled,
      ],
      _getStyleForRootBasedOnPosition(labelPosition),
    ],
    input: [
      {
        minWidth: 0,
        boxSizing: 'border-box',
        boxShadow: 'none',
        borderStyle: 'none',
        borderRadius: 'none',
        margin: 0,
        fontSize: 14,
        flex: 'none',
        color: standardControlSemanticColors.inputTextColor,
        backgroundColor: 'transparent', // use backgroundColor set in spinButtonWrapper
        height: '100%',
        paddingTop: 3,
        paddingRight: 3,
        paddingBottom: 4,
        paddingLeft: 4,
        outline: 0,
        textOverflow: 'ellipsis',
        display: 'block',
        float: 'left',
        width: 'calc(100% - 14px)',
        overflow: 'hidden',
        cursor: 'text',
        userSelect: 'text',
      },
      disabled && [spinButtonDisabledStyles(theme), commonHighContrastDisabledStyles],
      !disabled && {
        '::selection': {
          backgroundColor: standardControlSemanticColors.inputSelectionBackgroundColor,
          color: standardControlSemanticColors.inputSelectionTextColor,
          selectors: {
            [HighContrastSelector]: {
              backgroundColor: 'Highlight',
              borderColor: 'Highlight',
              color: 'HighlightText',
            },
          },
        },
      },
    ],
    icon: [
      {
        padding: '0 10px 0 0',
        height: 20,
        width: 20,
        lineHeight: 20,
        fontSize: 20,
      },
      disabled && [
        {
          opacity: 0.4,
          color: standardControlSemanticColors.textDisabledColor,
        },
        commonHighContrastDisabledStyles,
      ],
    ],
    arrowButtonsContainer: [
      {
        outline: 'none',
        fontSize: 12,
        display: 'block',
        float: 'left',
        height: '100%',
        cursor: 'default',
        padding: 0,
        boxSizing: 'border-box',
        ':hover': {
          backgroundColor: standardControlSemanticColors.spinButtonContainerContainerHoverBackground,
          selectors: {
            button: {
              selectors: {
                [HighContrastSelector]: {
                  border: '1px solid Highlight',
                },
              },
            },
          },
        },
        button: {
          border: 'none',
        },
      },
      isFocused && { backgroundColor: standardControlSemanticColors.spinButtonArrowBoxFocusedBackgroundColor },
      disabled && [spinButtonDisabledStyles(theme), commonHighContrastDisabledStyles],
    ],
  };
};

/**
 * Returns the display style for root according to position of the label
 */
const getDisplayStyleForRoot = (labelPosition: Position): IStyle => {
  switch (labelPosition) {
    case Position.top:
    case Position.bottom:
      return;
    default:
      return { display: 'flex' };
  }
};

/**
 * Returns the Style corresponding to the label position
 */
const _getStyleForRootBasedOnPosition = (labelPosition: Position): IStyle => {
  switch (labelPosition) {
    case Position.top:
    case Position.bottom:
      return { width: '100%' };
    default:
      return {};
  }
};

export const getSpinButtonStyles = (
  customStyles: Partial<ISpinButtonStyles> | undefined,
  theme: ITheme,
  disabled: boolean,
  isFocused: boolean,
  labelPosition: Position = Position.start
): Partial<ISpinButtonStyles> => {
  return concatStyleSets(spinButtonStyles(theme, disabled, isFocused, labelPosition), customStyles);
};

export const getArrowButtonStyles = memoizeFunction(
  (theme: ITheme, customStyles?: Partial<IButtonStyles>): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors = getStandardControlSemanticColorsWithTheme(
      theme
    );
    return concatStyleSets(
      {
        root: {
          width: 14,
          minWidth: 14,
        },
        rootHovered: [
          {
            backgroundColor: standardControlSemanticColors.inputButtonHoverBackground,
            selectors: {
              // Increase specificity otherwise the input hover will take precedence over
              // these hover styles
              '&&:hover': [
                {
                  backgroundColor: standardControlSemanticColors.inputButtonHoverBackground,
                },
                commonHighContrastHoverFocusStyles,
              ],
            },
          },
          commonHighContrastHoverFocusStyles,
        ],
        rootPressed: [
          {
            backgroundColor: standardControlSemanticColors.inputButtonPressedBackgroundColor,
            selectors: {
              // Increase specificity otherwise the input press will take precedence over
              // these active styles
              '&&:active': [
                {
                  backgroundColor: standardControlSemanticColors.inputButtonPressedBackgroundColor,
                },
                commonHighContrastHoverFocusStyles,
              ],
            },
          },
          commonHighContrastHoverFocusStyles,
        ],
        icon: {
          height: 8,
          width: 8,
          lineHeight: 8,
          fill: standardControlSemanticColors.inputTextColor,
        },
      },
      customStyles
    );
  }
);
